/**
 * Renders the other participant's cursor (pen or eraser icon) at the correct position.
 * Uses logical coords and container ref to compute pixel position.
 */

import React, { useState, useLayoutEffect, useRef } from 'react';
import { logicalToScreen } from './coordinateUtils';
import './RemoteCursor.css';

function RemoteCursor({ containerRef, cursor }) {
  const [pixel, setPixel] = useState(null);
  const observerRef = useRef(null);

  useLayoutEffect(() => {
    const container = containerRef?.current;
    if (!container) {
      setPixel(null);
      return undefined;
    }

    const update = () => {
      if (!cursor || typeof cursor.x !== 'number' || typeof cursor.y !== 'number') {
        setPixel(null);
        return;
      }
      const rect = container.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      if (w <= 0 || h <= 0) {
        setPixel(null);
        return;
      }
      const p = logicalToScreen(cursor.x, cursor.y, w, h);
      setPixel(p ? { x: p.x, y: p.y, tool: cursor.tool } : null);
    };

    update();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(container);
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [containerRef, cursor]);

  if (!pixel) return null;

  const isEraser = pixel.tool === 'eraser';
  const hotspot = isEraser ? 12 : 4;
  const hotspotY = isEraser ? 12 : 20;

  return (
    <div
      className="remote-cursor"
      style={{
        left: pixel.x - hotspot,
        top: pixel.y - hotspotY,
        width: isEraser ? 24 : 20,
        height: isEraser ? 24 : 28,
        pointerEvents: 'none',
      }}
      aria-hidden
    >
      <img
        src={isEraser ? '/cursors/eraser.svg' : '/cursors/pen.svg'}
        alt=""
        className="remote-cursor__icon"
        width={isEraser ? 24 : 20}
        height={isEraser ? 24 : 28}
      />
    </div>
  );
}

export default React.memo(RemoteCursor);
